import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LayoutDashboard, Wallet, FileText, Key, Settings as SettingsIcon, Book, BookOpen, ChevronDown, Send, Bell, Activity, Church } from 'lucide-react';

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [hasCongregations, setHasCongregations] = useState(false);

  useEffect(() => {
    loadProjects();
    loadCongregationsFlag();
    
    // Cargar proyecto activo del localStorage
    const savedProjectId = localStorage.getItem('activeProjectId');
    if (savedProjectId && savedProjectId !== 'all') {
      const project = projects.find(p => p.project_id === savedProjectId);
      if (project) setActiveProject(project);
    }
  }, []);

  const loadCongregationsFlag = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/merchant/congregations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setHasCongregations((data?.total_congregations || 0) > 0);
    } catch (error) {
      // silencioso: comercios sin congregaciones simplemente no ven el menú
    }
  };

  const loadProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setProjects(data);
      
      // Si hay proyectos y no hay uno activo, seleccionar el primero SIN recargar
      const savedProjectId = localStorage.getItem('activeProjectId');
      if (data.length > 0 && !savedProjectId) {
        selectProject(data[0], { reload: false });
      } else if (savedProjectId && savedProjectId !== 'all') {
        const project = data.find(p => p.project_id === savedProjectId);
        if (project) setActiveProject(project);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const selectProject = (project, { reload = true } = {}) => {
    setActiveProject(project);
    localStorage.setItem('activeProjectId', project?.project_id || 'all');
    setShowProjectSelector(false);
    // Recargar solo en cambios iniciados por el usuario. En la auto-selección
    // inicial NO recargamos: bajo React StrictMode el reload abortaba el fetch
    // en vuelo del segundo montaje y generaba "TypeError: Failed to fetch".
    if (reload) window.location.reload();
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: BookOpen, label: 'Estado de Cuenta', path: '/ledger' },
    ...(hasCongregations ? [{ icon: Church, label: 'Congregaciones', path: '/congregations' }] : []),
    { icon: Wallet, label: 'Wallets', path: '/wallets', projectRequired: true },
    { icon: FileText, label: 'Órdenes de Pago', path: '/orders', projectRequired: true },
    { icon: Send, label: 'Transferencias', path: '/transfers' },
    { icon: Bell, label: 'Webhooks', path: '/webhooks' },
    { icon: Activity, label: 'API Health', path: '/integration-health' },
    { icon: Key, label: 'API Keys', path: '/api-keys' },
    { icon: Book, label: 'Documentación', path: '/documentation' },
    { icon: SettingsIcon, label: 'Configuración', path: '/settings' }
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('activeProjectId');
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b sticky top-0 z-40" style={{ backgroundColor: '#051C2C' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            {/* Logo y Menu Toggle */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-white hover:text-gray-300 lg:hidden"
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              
              <img 
                src="/logos/logo-fondo-oscuro.png" 
                alt="Caura Pay" 
                className="h-7 cursor-pointer"
                onClick={() => navigate('/dashboard')}
              />
            </div>

            {/* Project Selector */}
            <div className="flex-1 max-w-xs mx-4">
              <div className="relative">
                <button
                  onClick={() => setShowProjectSelector(!showProjectSelector)}
                  className="w-full flex items-center justify-between px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors"
                >
                  <span className="text-sm font-medium truncate">
                    {activeProject ? activeProject.project_name : 'Todos los Proyectos'}
                  </span>
                  <ChevronDown className="w-4 h-4 ml-2" />
                </button>

                {showProjectSelector && (
                  <div className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-xl border border-gray-200 py-1 max-h-64 overflow-y-auto z-50">
                    <button
                      onClick={() => selectProject(null)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                        !activeProject ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      Todos los Proyectos
                    </button>
                    <div className="border-t border-gray-200 my-1"></div>
                    {projects.map((project) => (
                      <button
                        key={project.project_id}
                        onClick={() => selectProject(project)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                          activeProject?.project_id === project.project_id 
                            ? 'bg-blue-50 text-blue-700 font-medium' 
                            : 'text-gray-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{project.project_name}</span>
                          <span className="text-xs text-gray-500">{project.fee_percentage}%</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* User Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-white border border-white/30 rounded-lg hover:bg-white/10 transition-colors"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar for Desktop */}
      <div className="flex">
        <aside className={`
          fixed lg:sticky top-[57px] left-0 h-[calc(100vh-57px)] w-64 border-r border-gray-200 bg-white z-30 transform transition-transform lg:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <nav className="p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const disabled = item.projectRequired && !activeProject;
              
              return (
                <button
                  key={item.path}
                  onClick={() => !disabled && navigate(item.path)}
                  disabled={disabled}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'text-white'
                      : disabled
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  style={isActive(item.path) ? { backgroundColor: '#003594' } : {}}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{item.label}</span>
                  {disabled && (
                    <span className="ml-auto text-xs text-gray-400">Proyecto requerido</span>
                  )}
                </button>
              );
            })}

            {/* Project Management */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <button
                onClick={() => navigate('/projects')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === '/projects'
                    ? 'text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                style={location.pathname === '/projects' ? { backgroundColor: '#008AD8' } : {}}
              >
                <span className="text-sm font-medium">📁 Gestionar Proyectos</span>
              </button>
            </div>

            {/* Active Project Info */}
            {activeProject && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="px-4 py-2 bg-blue-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Proyecto Activo:</p>
                  <p className="text-sm font-semibold text-blue-900 truncate">{activeProject.project_name}</p>
                  <p className="text-xs text-blue-700 mt-1">Comisión: {activeProject.fee_percentage}%</p>
                </div>
              </div>
            )}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-h-[calc(100vh-57px)]">
          {children}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
